import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllDataReportList } from 'app/api/report';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const useOverAllDataReport = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const [values, setValues] = useState({
    mallName: '',
    firstBillDateStart: '',
    firstBillDateEnd: '',
    invoice_dateStart: '',
    invoice_dateEnd: '',
    membershipType: '',
  });
  const [totalNumber, setTotalNumber] = useState(0);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const currentPage = 1;
  const dataPerPage = 10;
  const [isFiltering, setIsFiltering] = useState(false);
  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const sectionObj = {
    sectionTitle: 'Over all Accepted bills Data Report',
    searchObj: {
      keys: ['invoice_number', 'shopping_amount', 'storeName', 'phoneNumber'],
      placeholder: 'Search  Name, Store, Mall, Contact No.',
      searchType: 'server',
      handleSearch: (e) => {
        console.log('Handle search: ', e);
      },
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => {
          setIsFilterOpen(!isFilterOpen);
        },
      },
    ],
    downloadObj: {
      excelClick: (e) => {
        handleDownloadexcel(e);
      },
      csvClick: (e) => {
        handleDownloadcsv(e);
      },

      pdfClick: () => () => {},
      inProgress: downloadInProgress,
    },
  };

  const fetchReportData = useCallback(
    async (
      page,
      search = '',
      filters = {},
      download = false,
      downloadType = ''
    ) => {
      setPageLoading(true);
      try {
        let requestParams = {
          page,
          limit: dataPerPage,
          search,
          mallName: filters.mallName,
          firstBillDateStart: filters.firstBillDateStart,
          firstBillDateEnd: filters.firstBillDateEnd,
          invoice_dateStart: filters.invoice_dateStart,
          invoice_dateEnd: filters.invoice_dateEnd,
          membershipType: filters.membershipType,
        };

        const response = await getAllDataReportList(requestParams);
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.data || [];
          const newArray = dataArr.map((item) => ({
            ...item,
          }));

          setData(newArray);
          setFilteredData(newArray);

          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );
  useEffect(() => {
    if (isFiltering) {
      handleFiltering();
      fetchReportData(1, '', values);
      setIsFiltering(false); // Reset filtering flag after filtering is completed
    }
  }, [isFiltering, data, values, filteredData]);

  function handleFiltering() {
    const filtered = data.filter((item) => {
      let isMallMatching =
        !values.mallName ||
        item?.mallName?.toLowerCase().includes(values.mallName.toLowerCase());

      let isFirstBillDateStartMatching =
        !values.firstBillDateStart ||
        new Date(item?.firstBillDate) >= new Date(values.firstBillDateStart);

      let isFirstBillDateEndMatching =
        !values.firstBillDateEnd ||
        new Date(item?.firstBillDate) <= new Date(values.firstBillDateEnd);

      let isInvoiceDateStartMatching =
        !values.invoice_dateStart ||
        new Date(item?.invoice_date) >= new Date(values.invoice_dateStart);

      let isInvoiceDateEndMatching =
        !values.invoice_dateEnd ||
        new Date(item?.invoice_date) <= new Date(values.invoice_dateEnd);

      let isMembershipTypeMatching =
        !values.membershipType ||
        item?.membershipType
          ?.toLowerCase()
          .includes(values.membershipType.toLowerCase());
      return (
        isMallMatching &&
        isFirstBillDateStartMatching &&
        isFirstBillDateEndMatching &&
        isInvoiceDateStartMatching &&
        isInvoiceDateEndMatching &&
        isMembershipTypeMatching
      );
    });

    setFilteredData(filtered);
  }
  function handleSubmit(event) {
    event.preventDefault();
    setIsFiltering(true);
  }
  function resetfilterModel() {
    setValues({
      mallName: '',
      firstBillDateStart: '',
      firstBillDateEnd: '',
      invoice_dateStart: '',
      invoice_dateEnd: '',
      membershipType: '',
    });
    setFilteredData([]);
    setIsFiltering(true);
  }
  function handleFilterCancel() {
    setIsFilterOpen(false);
  }
  // Async export: returns a jobId, then polls /export/status/:jobId. We DON'T
  // block the UI with pageLoading — the Toast is enough feedback, and forcing
  // a full-page loader prevents the user from working while the export runs
  // (which can take minutes for large datasets).
  async function startDownload(downloadType) {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);
    let filters = values;
    let requestParams = {
      page: 1,
      limit: totalNumber,
      search: '',
      mallName: filters.mallName,
      firstBillDateStart: filters.firstBillDateStart,
      firstBillDateEnd: filters.firstBillDateEnd,
      invoice_dateStart: filters.invoice_dateStart,
      invoice_dateEnd: filters.invoice_dateEnd,
      membershipType: filters.membershipType,
      download: true,
      downloadType,
    };

    try {
      const response = await getAllDataReportList(requestParams);
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        downloadInProgressRef.current = false;
        setDownloadInProgress(false);
        return;
      }

      Toast.success('Report generation started');

      startExportPoll(jobId, {
        onDone: (url) => {
          triggerDownload(url);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onFailed: (error) => {
          Toast.error(error || Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
      });
    } catch (error) {
      console.error('startDownload failed:', error);
      Toast.error(Message.Error.COMMON_MESSAGE);
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    }
  }

  async function handleDownloadcsv() {
    return startDownload('csv');
  }

  async function handleDownloadexcel() {
    return startDownload('excel');
  }
  return {
    pageLoading,
    filteredData,
    navigate,
    totalNumber,
    currentPage,
    dataPerPage,
    pageCount,
    sectionObj,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    resetfilterModel,
    handleSubmit,
    handleFilterCancel,
    fetchReportData,
    isFiltering,
  };
};

export default useOverAllDataReport;
